// Voice-leading progressions, ranked by voice-leading efficiency. Built around
// a small catalog of the idioms the Jazz 1460 corpus surfaces as its most
// common moves (see src/music/corpus.ts): the ii-V-I and its relatives, the
// turnarounds, and the two commonest substitutions. Each idiom searches every
// available top voice in one voicing family and ranks the results by movement.

import type { ChordTypeId } from './chords';
import { chordSymbol } from './chords';
import { activeStringsForFingering, type StringSet } from './tuning';
import { familiesOf, type VoicingFamily, type VoicingFamilySelection } from './voicingFamilies';
import {
  generateChordVoicing,
  shouldUseVoicing,
  voicingIdentityLabel,
  type ChordVoicing,
  type Fingering,
  type Inversion,
} from './voicing';

export type ProgressionType = 'major' | 'minor';

/** Harmonic rhythm: how the chords sit in the bar. Long = one bar each; short
 *  packs the pre-tonic chords two-to-a-bar (the "short ii-V"). */
export type HarmonicRhythm = 'long' | 'short';

/** One chord of a progression, in the catalog's reference key. */
export interface ProgressionStep {
  root: string;
  type: ChordTypeId;
}

/** A named idiom from the catalog. Roots are written in C (major) or C minor. */
export interface ProgressionDef {
  id: string;
  /** Menu label, e.g. "Major ii-V-I". */
  name: string;
  /** Degree reading for the sub-heading, e.g. "ii · V · I". */
  degrees: string;
  steps: ProgressionStep[];
  /** Ends on a tonic — a short rhythm then gives the last chord a full bar. */
  resolves: boolean;
}

export interface ProgressionChord {
  root: string;
  type: ChordTypeId;
  inversion: Inversion;
  stringSet: StringSet;
  voicing: ChordVoicing;
  symbol: string;
  label: string;
}

export interface Progression {
  chords: ProgressionChord[];
  totalDistance: number;
  stringSetPattern: string;
}

/**
 * The progression catalog. Roots are fixed in the reference key (C major / C
 * minor) so the shapes read consistently; the optimiser searches every
 * top voice. Ordering roughly follows how often the corpus plays each idiom.
 */
export const PROGRESSION_CATALOG: ProgressionDef[] = [
  {
    id: 'major-ii-v-i',
    name: 'Major ii-V-I',
    degrees: 'ii · V · I',
    steps: [
      { root: 'D', type: 'min7' },
      { root: 'G', type: 'dom7' },
      { root: 'C', type: 'maj7' },
    ],
    resolves: true,
  },
  {
    id: 'minor-ii-v-i',
    name: 'Minor ii-V-i',
    degrees: 'iiø · V · i',
    steps: [
      { root: 'D', type: 'min7b5' },
      { root: 'G', type: 'dom7' },
      { root: 'C', type: 'min7' },
    ],
    resolves: true,
  },
  {
    id: 'ii-v',
    name: 'ii-V',
    degrees: 'ii · V',
    steps: [
      { root: 'D', type: 'min7' },
      { root: 'G', type: 'dom7' },
    ],
    resolves: false,
  },
  {
    id: 'v-i',
    name: 'V-I',
    degrees: 'V · I',
    steps: [
      { root: 'G', type: 'dom7' },
      { root: 'C', type: 'maj7' },
    ],
    resolves: true,
  },
  {
    id: 'turnaround-i-vi-ii-v',
    name: 'Turnaround I-VI-ii-V',
    degrees: 'I · VI · ii · V',
    steps: [
      { root: 'C', type: 'maj7' },
      { root: 'A', type: 'dom7' },
      { root: 'D', type: 'min7' },
      { root: 'G', type: 'dom7' },
    ],
    resolves: false,
  },
  {
    id: 'turnaround-iii-vi-ii-v',
    name: 'Turnaround iii-VI-ii-V',
    degrees: 'iii · VI · ii · V',
    steps: [
      { root: 'E', type: 'min7' },
      { root: 'A', type: 'dom7' },
      { root: 'D', type: 'min7' },
      { root: 'G', type: 'dom7' },
    ],
    resolves: false,
  },
  {
    id: 'backdoor-ii-v',
    name: 'Backdoor ii-V',
    degrees: 'iv · ♭VII · I',
    steps: [
      { root: 'F', type: 'min7' },
      { root: 'Bb', type: 'dom7' },
      { root: 'C', type: 'maj7' },
    ],
    resolves: true,
  },
  {
    id: 'tritone-sub-ii-v-i',
    name: 'Tritone-sub ii-V-I',
    degrees: 'ii · ♭II · I',
    steps: [
      { root: 'D', type: 'min7' },
      { root: 'Db', type: 'dom7' },
      { root: 'C', type: 'maj7' },
    ],
    resolves: true,
  },
];

/** Beats per chord for a given harmonic rhythm (4/4 assumed). */
export function rhythmBeats(def: ProgressionDef, rhythm: HarmonicRhythm): number[] {
  const n = def.steps.length;
  if (rhythm === 'long') return Array(n).fill(4);
  // Short: two chords to a bar. A resolving idiom of three chords or more lands
  // its tonic on a full bar (the classic "short ii-V" into a whole-bar I); the
  // two-chord cadences just fill a single bar, so every idiom sits on whole bars.
  const beats = Array(n).fill(2);
  if (def.resolves && n >= 3) beats[n - 1] = 4;
  return beats;
}

interface ActiveNote {
  string: number;
  fret: number;
}

function activeNotes(fingering: Fingering): ActiveNote[] {
  const notes: ActiveNote[] = [];
  for (const stringIndex of activeStringsForFingering(fingering)) {
    const fret = fingering.frets[stringIndex];
    if (fret !== null && fret !== undefined) {
      notes.push({ string: stringIndex, fret });
    }
  }
  return notes;
}

/** Voice-leading distance allowing for string-set changes between two chords. */
function crossStringSetDistance(a: ProgressionChord, b: ProgressionChord): number {
  const notes1 = activeNotes(a.voicing);
  const notes2 = activeNotes(b.voicing);
  if (notes1.length !== 4 || notes2.length !== 4) return 1000;

  let total = 0;
  for (let i = 0; i < 4; i++) {
    let distance = Math.abs(notes2[i].fret - notes1[i].fret);
    if (notes1[i].string !== notes2[i].string) distance += 3;
    total += distance;
  }
  if (a.stringSet !== b.stringSet) total += 2;
  return total;
}

function evaluatePattern(
  steps: ProgressionStep[],
  inversions: Inversion[],
  stringSets: StringSet[],
  avoidB9: boolean,
  voicingFamily: VoicingFamily,
): Progression | null {
  const chords: ProgressionChord[] = [];
  for (let i = 0; i < steps.length; i++) {
    const { root, type } = steps[i];
    const voicing = generateChordVoicing(root, type, inversions[i], stringSets[i], voicingFamily);
    if (!voicing || !shouldUseVoicing(voicing, stringSets[i], type, avoidB9)) {
      return null;
    }
    chords.push({
      root,
      type,
      inversion: inversions[i],
      stringSet: stringSets[i],
      voicing,
      symbol: chordSymbol(root, type),
      label: `${chordSymbol(root, type)} · ${voicingIdentityLabel(voicing, root, type)}`,
    });
  }

  let totalDistance = 0;
  for (let i = 0; i < chords.length - 1; i++) {
    totalDistance += crossStringSetDistance(chords[i], chords[i + 1]);
  }

  return { chords, totalDistance, stringSetPattern: stringSets.join('-') };
}

/** Every source-rotation/top-voice assignment for `n` chords (n-digit base-4 counter). */
function inversionCombos(n: number): Inversion[][] {
  const combos: Inversion[][] = [];
  const total = 4 ** n;
  for (let code = 0; code < total; code++) {
    const inv: Inversion[] = [];
    for (let i = 0; i < n; i++) inv.push(((code >> (2 * i)) & 3) as Inversion);
    combos.push(inv);
  }
  return combos;
}

/** String-set assignments: chord 0 stays on `home`, the rest range over `choices`. */
function stringSetPatterns(n: number, home: StringSet, choices: StringSet[]): StringSet[][] {
  let patterns: StringSet[][] = [[home]];
  for (let i = 1; i < n; i++) {
    const next: StringSet[][] = [];
    for (const p of patterns) for (const c of choices) next.push([...p, c]);
    patterns = next;
  }
  return patterns;
}

/**
 * Generate the top `limit` voicings of an arbitrary chord sequence for the given
 * string set, ranked by total voice-leading distance (lower is smoother). With
 * `freeStringSet` the first chord still starts on the given set, but later
 * chords may sit on either set — the ranking then weighs set hops against
 * slides up the neck.
 */
export function generateProgressionsFromSteps(
  steps: ProgressionStep[],
  stringSet: StringSet,
  options: {
    avoidB9?: boolean;
    limit?: number;
    freeStringSet?: boolean;
    voicingFamily?: VoicingFamilySelection;
  } = {},
): Progression[] {
  const avoidB9 = options.avoidB9 ?? true;
  const limit = options.limit ?? 4;
  // A combined selection ranks each family's patterns in ONE pool. Patterns
  // stay family-pure here — per-step mixing is the line optimiser's job; this
  // page enumerates canonical shapes.
  const families = familiesOf(options.voicingFamily ?? 'drop-2');
  const n = steps.length;
  if (n === 0) return [];

  const setChoices: StringSet[] = options.freeStringSet ? ['middle', 'upper'] : [stringSet];
  const patterns = stringSetPatterns(n, stringSet, setChoices);
  const invCombos = inversionCombos(n);

  const all: Progression[] = [];
  for (const voicingFamily of families) {
    for (const pattern of patterns) {
      for (const inv of invCombos) {
        const progression = evaluatePattern(steps, inv, pattern, avoidB9, voicingFamily);
        if (progression) all.push(progression);
      }
    }
  }

  all.sort((a, b) => a.totalDistance - b.totalDistance);
  return all.slice(0, limit);
}

const IIVI_STEPS: Record<ProgressionType, ProgressionStep[]> = {
  major: PROGRESSION_CATALOG[0].steps,
  minor: PROGRESSION_CATALOG[1].steps,
};

/**
 * Generate the top `limit` ii-V-I patterns for the given string set, ranked by
 * total voice-leading distance. Kept for callers that just want the cadence;
 * the general engine is `generateProgressionsFromSteps`.
 */
export function generateProgressions(
  type: ProgressionType,
  stringSet: StringSet,
  options: {
    avoidB9?: boolean;
    limit?: number;
    freeStringSet?: boolean;
    voicingFamily?: VoicingFamily;
  } = {},
): Progression[] {
  return generateProgressionsFromSteps(IIVI_STEPS[type], stringSet, options);
}
